import React, { useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import {
  setAudioOutput,
  setLocalVideoTrackInfo,
  setLocalAudioTrackInfo,
} from "store/local-tracks/slice";
import { Preview } from "components/shared/preview/Preview";
import { Select } from "components/shared/input/select/Select";
import styles from "./SelectMediaDevices.module.css";
import { removeErrorNotification } from "store/errors/slice";
import { SetTrackPayload } from "store/local-tracks/types";
import { PayloadActionCreator } from "@reduxjs/toolkit";
import { MediaDeviceInfoLite } from "store/media-devices/types";
import { toggleAudioTestDialog } from "store/user/slice";

interface SelectMediaDevicesProps {
  showPreview?: boolean;
  showMicTest?: boolean;
}

export const SelectMediaDevices: React.FC<SelectMediaDevicesProps> = ({
  showPreview,
  showMicTest,
}) => {
  const dispatch = useDispatch();
  const mediaDevices = useSelector((s: RootState) => s.mediaDevices);
  const localTracks = useSelector((s: RootState) => s.localTracks);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioSrcRef = useRef<HTMLSourceElement>(null);

  useEffect(() => {
    if ((audioRef as any).current?.setSinkId) {
      (audioRef as any).current.setSinkId(localTracks.audioOutput?.deviceId);
    }
  }, [localTracks.audioOutput]);

  const playSound = async () => {
    if (audioRef.current) {
      try {
        audioRef.current.load();
        await audioRef.current.play();
      } catch (e) {
        console.error("Could not play test sound: ", e);
      }
    }
  };

  const changeDevice = useCallback(
    async (
      setFn: PayloadActionCreator<SetTrackPayload>,
      inputs: MediaDeviceInfoLite[],
      deviceId: string
    ) => {
      dispatch(removeErrorNotification());

      const selected = inputs.find((vi) => vi.deviceId === deviceId);
      if (selected) {
        dispatch(
          setFn({
            deviceId: selected.deviceId,
            label: selected.label,
            isDefault: selected.isDefault
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <>
      {localTracks.videoTrackInfo && showPreview && <Preview />}
      <Select
        iconId="video-camera"
        iconClassName={styles["white-fill"]}
        id="video-input"
        selected={localTracks.videoTrackInfo?.deviceId}
        options={mediaDevices.videoInputs.map((vi) => {
          return { name: vi.label, value: vi.deviceId };
        })}
        onChange={(event) =>
          changeDevice(
            setLocalVideoTrackInfo,
            mediaDevices.videoInputs,
            event.target.value
          )
        }
        disabled={localTracks.busy.video}
      />
      <div className={styles["speaker-select-wrapper"]}>
        <Select
          iconId="microphone"
          iconClassName={styles["white-fill"]}
          id="audio-input"
          selected={localTracks.audioTrackInfo?.deviceId}
          options={mediaDevices.audioInputs.map((ai) => {
            return { name: ai.label, value: ai.deviceId };
          })}
          onChange={(event) =>
            changeDevice(
              setLocalAudioTrackInfo,
              mediaDevices.audioInputs,
              event.target.value
            )
          }
          disabled={localTracks.busy.audio}
        />
        {showMicTest && (
          <button
            type="button"
            className="btn btn-outline-primary ml-2 mb-3"
            onClick={() => dispatch(toggleAudioTestDialog())}
          >
            Test
          </button>
        )}
      </div>
      {mediaDevices.audioOutputs.length > 0 && (
        <>
          <div className={styles["speaker-select-wrapper"]}>
            <Select
              iconId="speaker"
              id="audio-output"
              selected={localTracks.audioOutput?.deviceId}
              options={mediaDevices.audioOutputs.map((ao) => {
                return { name: ao.label, value: ao.deviceId };
              })}
              onChange={(event) =>
                changeDevice(
                  setAudioOutput,
                  mediaDevices.audioOutputs,
                  event.target.value
                )
              }
            />
            <button
              type="button"
              className="btn btn-outline-primary ml-2 mb-3"
              onClick={playSound}
            >
              Test
            </button>
          </div>
          <audio id="test" ref={audioRef}>
            <source
              ref={audioSrcRef}
              src="https://switcherstudio.blob.core.windows.net/staticcontent/audio/chime.mp3"
            ></source>
          </audio>
        </>
      )}
    </>
  );
};
